// Testimonial moderation (FR-70, FR-71): stories wait here until a platform
// administrator approves them for the home page or rejects them.
import { Badge, Button, Group, Loader, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";

type Testimonial = Schemas["Testimonial"];

const KEY = ["admin", "testimonials"] as const;

export function AdminTestimonialsPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")} intro={t("admin_testimonials.intro")}>
      <Queue />
    </AdminLayout>
  );
}

function Queue() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const queue = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/admin/testimonials", { params: { query: { limit: 100 } } })),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = queue.data?.items ?? [];

  const decide = async (s: Testimonial, decision: "approve" | "reject") => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.POST("/admin/testimonials/{testimonial_id}/decision", {
      params: { path: { testimonial_id: s.id } },
      body: { decision },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setNotice(
      data.status === "approved"
        ? t("admin_testimonials.approved", { name: data.display_name })
        : t("admin_testimonials.rejected", { name: data.display_name }),
    );
    void queryClient.invalidateQueries({ queryKey: KEY });
  };

  return (
    <Stack gap="md">
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {queue.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : queue.isError ? (
        <FormError message={errorMessage(queue.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="testimonials-empty">
          {t("admin_testimonials.empty")}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {items.map((s) => (
            <Paper key={s.id} withBorder p="md" radius="md" data-testid="testimonial-row">
              <Stack gap="xs">
                <Group justify="space-between" gap="xs">
                  <Text fw={600} style={{ overflowWrap: "anywhere" }}>
                    {s.display_name}
                  </Text>
                  <Badge variant="light" aria-label={t("admin_testimonials.language")}>
                    {t(`language_name.${s.locale}`, { defaultValue: s.locale })}
                  </Badge>
                </Group>
                <Text size="sm" lang={s.locale} style={{ overflowWrap: "anywhere" }}>
                  {s.body}
                </Text>
                <Text size="xs" c="dimmed">
                  {t("admin_testimonials.submitted", { date: f.dateTime(s.created_at) })}
                </Text>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => void decide(s, "approve")}
                    aria-label={t("admin_testimonials.approve_for", { name: s.display_name })}
                  >
                    {t("admin_testimonials.approve")}
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={() => void decide(s, "reject")}
                    aria-label={t("admin_testimonials.reject_for", { name: s.display_name })}
                  >
                    {t("admin_testimonials.reject")}
                  </Button>
                </Group>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
